import React, { memo } from "react";
import {
  View,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
} from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Label } from "@/components/ui/Label";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface AppTextAreaProps extends Omit<
  RNTextInputProps,
  "style" | "multiline" | "placeholder"
> {
  label: TranslationKeys;
  hint?: TranslationKeys;
  error?: TranslationKeys;
  /** Placeholder text — pass a translation key; the component translates it internally. */
  placeholder?: TranslationKeys;
  isRTL?: boolean;
  /**
   * Controls the visible height of the textarea.
   * Each line is approximately 24 px; default 5 lines ≈ 120 px (Figma Long Text spec).
   */
  numberOfLines?: number;
  /** Accessible label for the field (defaults to `label`). */
  accessibilityLabel?: TranslationKeys;
  /** Accessible hint for the field. */
  accessibilityHint?: TranslationKeys;
  testID?: string;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  containerClassName?: string;
}

/**
 * Multiline text area component.
 *
 * Mirrors the Figma "Input Field / Long Text" variant (node 10007:270598).
 * Shares the same label / hint / error anatomy as `AppTextInput` but renders
 * a taller, scrollable multiline field with text anchored to the top edge.
 *
 * Design tokens:
 *   - Container: `border border-stroke-50 rounded-md` (8 px radius)
 *   - Disabled bg: `bg-stroke-20`
 *   - Focus / active border: relies on native focus ring on web; no custom ring
 *
 * @example
 * <AppTextArea
 *   label="Description"
 *   value={value}
 *   onChangeText={setValue}
 *   placeholder="Enter description…"
 *   numberOfLines={5}
 * />
 */
export const AppTextArea = memo(function AppTextArea({
  label,
  hint,
  error,
  placeholder,
  isRTL,
  editable = true,
  numberOfLines = 5,
  accessibilityLabel,
  accessibilityHint,
  testID,
  required,
  containerClassName,
  ...rest
}: AppTextAreaProps) {
  const { t, isRTL: contextIsRTL } = useI18n();
  const resolvedIsRTL = isRTL ?? contextIsRTL;
  const a11yLabel = t(accessibilityLabel ?? label);

  const borderClass = error ? "border-error" : editable ? "border-stroke-50" : "border-stroke-20";
  const bgClass = editable ? "bg-white" : "bg-stroke-20";
  const textClass = editable ? "text-dark-text-50" : "text-dark-text-15";

  // 24 px per line matches the default line-height for `text-base` in Inter Display.
  const minHeight = numberOfLines * 24 + 24; // +24 for top/bottom padding

  return (
    <View className={cn("mb-4 w-full items-start", containerClassName)}>
      <Label label={label} required={required} />

      <View className={`w-full border rounded-md ${bgClass} ${borderClass}`} style={{ minHeight }}>
        <RNTextInput
          multiline
          textAlignVertical="top"
          editable={editable}
          textAlign={resolvedIsRTL ? "right" : "left"}
          testID={testID}
          placeholder={placeholder ? t(placeholder) : undefined}
          accessibilityLabel={a11yLabel}
          accessibilityHint={accessibilityHint ? t(accessibilityHint) : undefined}
          className={cn(
            "flex-1 py-3 px-4 text-base",
            resolvedIsRTL ? "text-end" : "text-start",
            textClass
          )}
          style={{ minHeight: minHeight - 2, textAlignVertical: "top" }}
          {...rest}
        />
      </View>

      {!!hint && !error && (
        <AppText className="text-xs text-dark-text-10 mt-1 text-start">{t(hint)}</AppText>
      )}
      {!!error && (
        <AppText role="alert" className="text-xs text-error mt-1 text-start">
          {t(error)}
        </AppText>
      )}
    </View>
  );
});
